import { Link as RouterLink } from "react-router-dom";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { CellsWrapper } from "../../../Components/FilterTable/Toolbar/CellsWrapper";
import { useCustomerId } from "../../../Components/hooks/useCustomerId";
import { ThreeDotsMenu } from "../../../Components/ThreeDotsMenu";
import { visuallyHiddenStyle } from "../../../constants/common";
import texts from "./texts";
import { type MyNotificationData } from "./types";
import { editUserNotification } from "./utils";

export const cyIds: Record<string, (rowId: string) => string> = {
  type: (rowId: string) => `type-${rowId}`,
  selectedNotifications: (rowId: string) => `selectedNotifications-${rowId}`,
  realTime: (rowId: string) => `realTime-${rowId}`,
  threeDots: (rowId: string) => `threeDots-${rowId}`,
};

const isInAppNotification = (
  notification: MyNotificationData
): notification is MyNotificationData & { allowRealTime: boolean } =>
  notification.id === "in-app" && notification.providerType === "In-app";

type Props = {
  row: MyNotificationData;
};

export const UserNotificationRow = ({ row }: Props) => {
  const customerId = useCustomerId();
  return (
    <CellsWrapper>
      <TableCell data-cy={cyIds.type(row.id)}>
        <Link component={RouterLink} to={editUserNotification(customerId, row.id)} color="inherit">
          {row.providerType}
        </Link>
      </TableCell>
      <TableCell>
        <Typography variant="body2" component="span" data-cy={cyIds.selectedNotifications(row.id)}>
          {row.selectedNotifications}
        </Typography>
        {isInAppNotification(row) && row.allowRealTime && (
          <>
            <Typography component="span" sx={visuallyHiddenStyle}>
              {texts.table.row.realTime.aria}
            </Typography>
            <Tooltip placement="bottom-start" title={texts.table.row.realTime.tooltip}>
              <Box component="span" aria-hidden data-cy={cyIds.realTime(row.id)}>
                <InfoIcon fontSize="inherit" sx={{ ml: 1, mb: -0.25 }} />
              </Box>
            </Tooltip>
          </>
        )}
      </TableCell>

      <TableCell padding="none" align="right" data-cy={cyIds.threeDots(row.id)}>
        <ThreeDotsMenu
          closeAfterSelect
          options={[
            {
              key: "1",
              label: (
                <Link
                  component={RouterLink}
                  to={
                    row.id === "emailDefault"
                      ? `/customers/${customerId}/user/notifications/create`
                      : editUserNotification(customerId, row.id)
                  }
                  color="inherit"
                  sx={{ textDecoration: "none" }}
                >
                  {texts.table.row.editNotification}
                </Link>
              ),
            },
          ]}
        />
      </TableCell>
    </CellsWrapper>
  );
};
